import {
	PAPER_WIDTHS_MM,
	printDocTypeSchema,
	printerConnectionSchema,
	printerStateSchema,
	printFailureReasonSchema,
	printJobStatusSchema,
	printModeSchema,
} from "@luckymenu/core";
import { z } from "zod";
import { DOCUMENT_KINDS, printDocumentSchema } from "./document";

/**
 * Inputs and outputs of `staff.printing.*`, shared by the API and the staff
 * apps. See docs/architecture/api.md, Printing (staff), and printing.md.
 */

/** A claim lasts this long; `startJob` renews it once the device starts sending. */
export const PRINT_LEASE_SECONDS = 30;
/** The device whose action made a job may claim it alone for this long. */
export const PRINT_HEAD_START_MS = 2000;
/** A job queued this long with nobody printing it shows "KOT not printed" (P5-T23). */
export const PRINT_NOT_PRINTED_AFTER_MS = 20_000;
/** Jobs are deleted after this many days. */
export const PRINT_JOB_RETENTION_DAYS = 7;

const uuid = z.uuid();

export const printerCapabilitiesSchema = z.object({
	cut: z.boolean(),
	drawer: z.boolean(),
	beep: z.boolean(),
});

export const paperWidthSchema = z.union([
	z.literal(PAPER_WIDTHS_MM[0]),
	z.literal(PAPER_WIDTHS_MM[1]),
]);

export const printStationSchema = z.object({
	deviceId: uuid,
	printerId: uuid,
	deviceName: z.string().nullable(),
	isReachable: z.boolean(),
	state: printerStateSchema,
	autoPrint: z.boolean(),
	lastCheckedAt: z.iso.datetime(),
});
export type PrintStation = z.infer<typeof printStationSchema>;

export const printerSchema = z.object({
	id: uuid,
	name: z.string(),
	connection: printerConnectionSchema,
	address: z.string().nullable(),
	paperWidthMm: paperWidthSchema,
	dotsPerLine: z.number().int(),
	mode: printModeSchema,
	capabilities: printerCapabilitiesSchema,
	isActive: z.boolean(),
	/** Devices that reported it, most recent first. */
	stations: z.array(printStationSchema),
	updatedAt: z.iso.datetime(),
});
export type Printer = z.infer<typeof printerSchema>;

/**
 * `staff.printing.listPrinters`: the restaurant's printers (deleted ones
 * left out) and where bills and each kitchen station's KOTs go.
 */
export const printerListSchema = z.object({
	printers: z.array(printerSchema),
	billPrinterId: uuid.nullable(),
	/** Copies of each bill printed by itself on the bill printer (P5-T25). */
	billCopies: z.number().int(),
	kitchenStations: z.array(
		z.object({
			id: uuid,
			name: z.string(),
			printerId: uuid.nullable(),
			/** Copies of each of its KOTs. */
			copies: z.number().int(),
		}),
	),
});
export type PrinterList = z.infer<typeof printerListSchema>;

const name = z.string().trim().min(1).max(60);

/**
 * `staff.printing.upsertPrinter`: creates or updates by the client-made `id`.
 * Fields left out keep their value, or take the default on create (80 mm,
 * text mode, a cutter). `dotsPerLine` follows the paper unless given.
 */
export const upsertPrinterInputSchema = z.object({
	id: uuid,
	name: name.optional(),
	connection: printerConnectionSchema.optional(),
	address: z.string().trim().max(200).nullable().optional(),
	paperWidthMm: paperWidthSchema.optional(),
	dotsPerLine: z.number().int().min(200).max(1200).optional(),
	mode: printModeSchema.optional(),
	capabilities: printerCapabilitiesSchema.optional(),
	isActive: z.boolean().optional(),
});
export type UpsertPrinterInput = z.input<typeof upsertPrinterInputSchema>;

export const deletePrinterInputSchema = z.object({ id: uuid });

/** Copies printed by themselves, each its own job (`copy_no`, printing.md, Copies). */
export const MAX_PRINT_COPIES = 9;
const copies = z.number().int().min(1).max(MAX_PRINT_COPIES);

/**
 * `staff.printing.assignPrinters` (MG-06, P5-T25): the bill printer and the
 * printer of each named kitchen station, and how many copies each prints.
 * Left out: unchanged. Null: none.
 */
export const assignPrintersInputSchema = z.object({
	billPrinterId: uuid.nullable().optional(),
	billCopies: copies.optional(),
	kitchenStations: z
		.array(
			z.object({
				kitchenStationId: uuid,
				printerId: uuid.nullable().optional(),
				copies: copies.optional(),
			}),
		)
		.max(50)
		.optional(),
});
export type AssignPrintersInput = z.input<typeof assignPrintersInputSchema>;

/**
 * `staff.printing.reportStation`: what this device can reach, on start and
 * every minute. Printers it no longer lists keep their last report.
 */
export const reportStationInputSchema = z.object({
	deviceId: uuid,
	deviceName: z.string().trim().max(60).nullable().optional(),
	printers: z
		.array(
			z.object({
				printerId: uuid,
				isReachable: z.boolean(),
				state: printerStateSchema.default("unknown"),
				autoPrint: z.boolean(),
			}),
		)
		.max(20),
});
export type ReportStationInput = z.input<typeof reportStationInputSchema>;

export const printJobSchema = z.object({
	id: uuid,
	printerId: uuid,
	docType: printDocTypeSchema,
	kind: z.enum(DOCUMENT_KINDS),
	refType: z.string(),
	refId: uuid.nullable(),
	copyNo: z.number().int(),
	isReprint: z.boolean(),
	/** "Duplicate" on a reprint. */
	copyLabel: z.string().nullable(),
	status: printJobStatusSchema,
	queuedAt: z.iso.datetime(),
	requestedBy: uuid.nullable(),
	requestedByDeviceId: uuid.nullable(),
	claimedByDeviceId: uuid.nullable(),
	claimedAt: z.iso.datetime().nullable(),
	leaseExpiresAt: z.iso.datetime().nullable(),
	sendingAt: z.iso.datetime().nullable(),
	attempts: z.number().int(),
	lastError: printFailureReasonSchema.nullable(),
	lastErrorDetail: z.string().nullable(),
	printedAt: z.iso.datetime().nullable(),
	createdAt: z.iso.datetime(),
});
export type PrintJob = z.infer<typeof printJobSchema>;

/** A job a station holds: what to print and on what. */
export const claimedPrintJobSchema = printJobSchema.extend({
	document: printDocumentSchema,
	printer: printerSchema.omit({ stations: true }),
});
export type ClaimedPrintJob = z.infer<typeof claimedPrintJobSchema>;

/**
 * `staff.printing.createJob`: prints a bill, a KOT or a test page. `id` is
 * made by the client: the same call again returns the same job. A bill or KOT
 * that has printed (or is printing) before prints as a reprint, marked
 * "Duplicate". The printer defaults to the bill printer or the KOT's station
 * printer; a test page names its printer in `refId`.
 *
 * `printedAt` reports a print the device already made without the API
 * (P5-T22, printing.md, Printing when the internet is down): the job is
 * recorded as done, held by the calling device (`x-device-id`), and nobody
 * prints it again.
 */
export const createPrintJobInputSchema = z.object({
	id: uuid,
	kind: z.enum(["bill", "kot", "test"]),
	refId: uuid,
	printerId: uuid.optional(),
	printedAt: z.iso.datetime().optional(),
});
export type CreatePrintJobInput = z.input<typeof createPrintJobInputSchema>;

/**
 * `staff.printing.claimJobs`: takes queued jobs of one printer with a
 * 30 second lease, oldest first. Only the device whose action made a job may
 * take it in its first 2 seconds. `jobIds` limits the claim to those jobs.
 */
export const claimJobsInputSchema = z.object({
	deviceId: uuid,
	printerId: uuid,
	jobIds: z.array(uuid).max(20).optional(),
	limit: z.number().int().min(1).max(10).default(1),
});
export type ClaimJobsInput = z.input<typeof claimJobsInputSchema>;

/** `startJob`, `completeJob`: the holder about to send bytes, or done. */
export const deviceJobInputSchema = z.object({ deviceId: uuid, jobId: uuid });

/** `staff.printing.failJob`: the holder could not print it. */
export const failJobInputSchema = z.object({
	deviceId: uuid,
	jobId: uuid,
	reason: printFailureReasonSchema.exclude(["may_have_printed"]),
	detail: z.string().trim().max(500).optional(),
});
export type FailJobInput = z.input<typeof failJobInputSchema>;

/** `retryJob` (failed or handled → queued), `cancelJob` ("Mark as handled"). */
export const jobInputSchema = z.object({ jobId: uuid });

/** `staff.printing.listJobs`: newest first. */
export const listJobsInputSchema = z.object({
	printerId: uuid.optional(),
	status: z.array(printJobStatusSchema).max(5).optional(),
	refType: z.enum(["bill", "kot", "printer", "day_closing"]).optional(),
	refId: uuid.optional(),
	/** Only jobs made after this time. */
	since: z.iso.datetime().optional(),
	limit: z.number().int().min(1).max(100).default(50),
});
export type ListJobsInput = z.input<typeof listJobsInputSchema>;
